import type {PlannerBrowserRecordRow} from './PlannerSidebarList';
import {buildPlannerPatientSelectionEvent} from './plannerPatientBrowser';
import {plannerEventDate} from './plannerDateTime';
import {formatPlannerSidebarTime} from './PlannerSidebarList';
import {
  buildPlannerEncounterDisplay,
  buildPlannerPatientDisplay,
  plannerEventAriaLabel,
  plannerEventConversationMetaText,
  plannerEventDurationText,
  plannerEventRowPills,
} from './plannerBrowserDisplay';

const resolveOption = (value: any, ...args: any[]) =>
  typeof value === 'function' ? value(...args) : value;

export function plannerEventToDatePatientRow(
  event: any,
  {
    cardKind = '',
    getRowProps,
    index = 0,
    isEventSelected,
    item = event,
    onSelect,
    preferTimestampTime = false,
    progressiveKey = 'planner-event',
    searchQuery = '',
    selectedEventId = null,
    showTimeColumn = true,
  }: Record<string, any> = {},
): PlannerBrowserRecordRow | null {
  if (!event) return null;

  const timestamp = plannerEventDate(event);
  const timestampTime = formatPlannerSidebarTime(timestamp);
  const time = preferTimestampTime
    ? timestampTime
    : event?.time || timestampTime;
  const ariaEvent = preferTimestampTime ? {...event, time} : event;
  const rowProps = resolveOption(getRowProps, event, item, index) || {};
  const patientDisplay = buildPlannerPatientDisplay(ariaEvent, {
    includeTimeInAria: showTimeColumn,
    searchQuery,
    showTimeColumn,
    time,
  });

  return {
    key: event?.id || `${progressiveKey}-${index}`,
    cardKind,
    identityRow: patientDisplay.identityRow,
    timestamp,
    time,
    name: patientDisplay.name,
    nameNode: patientDisplay.nameNode,
    language: patientDisplay.language,
    contextText: patientDisplay.contextText,
    contextNode: patientDisplay.contextNode,
    ariaLabel: patientDisplay.ariaLabel,
    isSelected: isEventSelected
      ? Boolean(isEventSelected(event, item, index))
      : Boolean(selectedEventId && event?.id === selectedEventId),
    onSelect: onSelect ? () => onSelect(event, item, index) : undefined,
    rowProps,
  };
}

export function plannerEventToDateEncounterRow(
  event: any,
  {
    cardKind = 'search',
    index = 0,
    onSelect,
    progressiveKey = 'planner-encounter',
    searchQuery = '',
    selectedEventId = null,
    showDateInTimeColumn = false,
  }: Record<string, any> = {},
): PlannerBrowserRecordRow | null {
  if (!event) return null;

  const display = buildPlannerEncounterDisplay(event, {
    index,
    progressiveKey,
    searchQuery,
    showDateInTimeColumn,
  });

  return {
    key: `${event?.id || `${progressiveKey}-${index}`}-encounter-row`,
    cardKind,
    encounterRows: display.encounterRows,
    showIdentityRow: false,
    timestamp: display.timestamp,
    time: display.time,
    timeDetail: display.timeDetail,
    name: display.name,
    nameNode: display.nameNode,
    language: '',
    contextText: '',
    detailLines: [],
    ariaLabel: display.ariaLabel,
    isSelected: Boolean(selectedEventId && event?.id === selectedEventId),
    onSelect: onSelect ? () => onSelect(event) : undefined,
    wrapName: true,
    rowProps: {
      'data-testid': 'planner-browser-encounter-child-row',
      singleLine: false,
    },
  };
}

export function plannerEventToDatePatientEncounterRow(
  event: any,
  {
    cardKind = 'recent',
    getRowProps,
    index = 0,
    onSelect,
    progressiveKey = 'planner-recent-encounter',
    selectedEventId = null,
  }: Record<string, any> = {},
): PlannerBrowserRecordRow | null {
  if (!event) return null;

  const display = buildPlannerEncounterDisplay(event, {
    index,
    mode: 'recent',
    progressiveKey,
  });
  const patientDisplay = buildPlannerPatientDisplay(event, {
    searchQuery: '',
    showTimeColumn: true,
    time: display.time,
  });
  const rowProps = resolveOption(getRowProps, event, event, index) || {};

  return {
    key: event?.id || `${progressiveKey}-${index}`,
    cardKind,
    encounterRows: display.encounterRows,
    identityRow: patientDisplay.identityRow,
    timestamp: display.timestamp,
    time: display.time,
    name: patientDisplay.name,
    language: patientDisplay.language,
    contextText: patientDisplay.contextText,
    detailLines: [],
    ariaLabel: plannerEventAriaLabel(
      {...event, time: display.time},
      {
        includeTime: true,
      },
    ).concat(display.ariaLabelSuffix ? ` ${display.ariaLabelSuffix}` : ''),
    isSelected: Boolean(selectedEventId && event?.id === selectedEventId),
    onSelect: onSelect ? () => onSelect(event) : undefined,
    rowProps: {
      ...rowProps,
      'data-testid':
        rowProps['data-testid'] || 'planner-browser-recent-encounter-row',
    },
  };
}

export function plannerRecentActivityToDatePatientEncounterRow(
  activity: any,
  {
    cardKind = 'recent',
    index = 0,
    onSelect,
    progressiveKey = 'planner-recent-activity',
    selectedEventId = null,
  }: Record<string, any> = {},
): PlannerBrowserRecordRow | null {
  if (!activity) return null;

  const timestamp = plannerEventDate(activity);
  const time = formatPlannerSidebarTime(timestamp);
  const patientDisplay = buildPlannerPatientDisplay(activity, {
    searchQuery: '',
    showTimeColumn: true,
    time,
  });
  const summaryText = String(
    activity?.summary || activity?.summaryText || '',
  ).trim();
  // Keep the "<duration> • Interpreter/Scribe" note in the aria label (so
  // screen readers still hear duration + modality) while the visible row shows
  // the neutral modality / Interpreter pills + a legible duration.
  const conversationMetaText = plannerEventConversationMetaText(activity);
  const pills = plannerEventRowPills(activity);
  const durationText = plannerEventDurationText(activity);
  const hasEncounterDetails = Boolean(
    summaryText || pills.length || durationText,
  );
  const encounterDetailText = [summaryText, conversationMetaText]
    .map(value => String(value || '').trim())
    .filter(Boolean)
    .join(' ');
  const encounterRows = hasEncounterDetails
    ? [
        {
          key: `${activity?.id || `${progressiveKey}-${index}`}-summary`,
          summary: summaryText || undefined,
          summaryNoOfLines: 2,
          summaryProminent: true,
          badges: [],
          pills,
          meta: durationText || undefined,
        },
      ]
    : [];

  return {
    key: activity?.id || `${progressiveKey}-${index}`,
    cardKind,
    encounterRows,
    identityRow: patientDisplay.identityRow,
    timestamp,
    time,
    name: patientDisplay.name,
    language: patientDisplay.language,
    contextText: patientDisplay.contextText,
    detailLines: [],
    ariaLabel: plannerEventAriaLabel(
      {...activity, time},
      {
        includeTime: true,
      },
    ).concat(encounterDetailText ? ` ${encounterDetailText}` : ''),
    isSelected: Boolean(selectedEventId && activity?.id === selectedEventId),
    onSelect: onSelect ? () => onSelect(activity) : undefined,
    rowProps: {
      'data-testid': 'planner-browser-recent-encounter-row',
    },
  };
}

export function patientItemToPatientRow(
  item: any,
  {
    getRowProps,
    index = 0,
    onSelectPatient,
    searchQuery = '',
    selectedPatientId = null,
    source = 'patient',
  }: Record<string, any> = {},
): PlannerBrowserRecordRow | null {
  const event = buildPlannerPatientSelectionEvent(item, {source});
  if (!event) return null;

  const rowProps = {
    'data-planner-patient-index': index,
    'data-planner-patient-anchor-id': `${source}-${item?.id}`,
    ...(resolveOption(getRowProps, event, item, index) || {}),
  };

  return plannerEventToDatePatientRow(event, {
    getRowProps: rowProps,
    index,
    isEventSelected: (rowEvent: any) =>
      Boolean(
        rowEvent?.patient?.id && rowEvent.patient.id === selectedPatientId,
      ),
    item,
    onSelect: () => onSelectPatient?.(item, source),
    progressiveKey: source,
    searchQuery,
    showTimeColumn: false,
    cardKind: source === 'dropdown' ? 'dropdown' : '',
  });
}
